import type { LocalVisitResource } from "~/hooks/visitDragDropReducer";
import type { OrgConfig } from "~/providers/OrgConfigProvider";

import {
  buildDriveOptimizerRequestData,
  effectiveShufflerLeadTimeMs,
  type DriveTimeOptimizerRequestData,
  type OptimizerInitialState,
} from "./driveTimeOptimizerConversion";
import { groupVisitResourcesByVisit } from "~/hooks/useVisitDragDrop";

type SerializedOptimizerInitialState = Omit<
  OptimizerInitialState,
  "schedulingCutoff" | "pastVisitResources" | "constrainedVisitResources" | "optimizableVisitResources"
> & {
  schedulingCutoff: string | Date;
  pastVisitResources: LocalVisitResource[];
  constrainedVisitResources: LocalVisitResource[];
  optimizableVisitResources: LocalVisitResource[];
};

const assertRecord = (value: unknown, label: string): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Expected ${label} to be an object.`);
  }

  return value as Record<string, unknown>;
};

const parseSchedulingCutoff = (value: unknown): Date => {
  if (!(typeof value === "string" || value instanceof Date)) {
    throw new Error("Expected schedulingCutoff to be a date string.");
  }

  const schedulingCutoff = new Date(value);
  if (Number.isNaN(schedulingCutoff.getTime())) {
    throw new Error("Expected schedulingCutoff to be a valid date.");
  }

  return schedulingCutoff;
};

export const parseOptimizerInitialStateJson = (rawJson: string): OptimizerInitialState => {
  const parsed = JSON.parse(rawJson) as unknown;
  const state = assertRecord(parsed, "optimizer initial state");

  return {
    ...(state as SerializedOptimizerInitialState),
    schedulingCutoff: parseSchedulingCutoff(state.schedulingCutoff),
  };
};

const assertVisitResourcesHaveVisit = (localVisitResources: LocalVisitResource[]): void => {
  for (const localVisitResource of localVisitResources) {
    if (!localVisitResource.visit) {
      throw new Error(`Visit resource ${localVisitResource.id} is missing its nested visit.`);
    }
  }
};

export const buildDriveOptimizerRequestDataFromInitialState = (
  optimizerInitialState: OptimizerInitialState
): DriveTimeOptimizerRequestData => {
  const localVisitResources = [
    ...(optimizerInitialState.pastVisitResources as LocalVisitResource[]),
    ...optimizerInitialState.constrainedVisitResources,
    ...optimizerInitialState.optimizableVisitResources,
  ];
  assertVisitResourcesHaveVisit(localVisitResources);
  const localVisits = groupVisitResourcesByVisit(localVisitResources);
  const capturedDateNowMs =
    optimizerInitialState.schedulingCutoff.getTime() -
    effectiveShufflerLeadTimeMs(optimizerInitialState.shufflerLeadTime);
  const originalDateNow = Date.now;

  Date.now = () => capturedDateNowMs;

  try {
    return buildDriveOptimizerRequestData(
      localVisits,
      optimizerInitialState.localCalendarBlocks,
      localVisitResources,
      optimizerInitialState.eligibleShifts,
      optimizerInitialState.careRequirementVisits,
      optimizerInitialState.orgConfig as OrgConfig,
      optimizerInitialState.shufflerLeadTime,
      optimizerInitialState.arrivalWindowAdjustment,
      optimizerInitialState.providers
    );
  } finally {
    Date.now = originalDateNow;
  }
};

export const buildDriveOptimizerRequestDataFromInitialStateJson = (
  rawJson: string
): DriveTimeOptimizerRequestData => {
  return buildDriveOptimizerRequestDataFromInitialState(parseOptimizerInitialStateJson(rawJson));
};

export const getSortedDriveOptimizerStartTimes = (
  requestData: DriveTimeOptimizerRequestData
): Date[] => {
  return requestData.input.visits
    .map((visit) => new Date(visit.startTime)) // this is actually the arrival window start time since this is in the optimizer context which doesn't know about the actual start time.
    .sort((left, right) => left.getTime() - right.getTime());
};

export const assertDriveOptimizerCanRun = (
  requestData: DriveTimeOptimizerRequestData,
  now: Date = new Date()
): void => {
  const [earliestArrivalWindowStartTime] = getSortedDriveOptimizerStartTimes(requestData);

  if (earliestArrivalWindowStartTime && earliestArrivalWindowStartTime < now) {
    throw new Error(
      `Cannot run optimizer because the earliest visit arrival window start time (${earliestArrivalWindowStartTime.toISOString()}) is before the current time (${now.toISOString()}).`
    );
  }
};
